import { notification } from 'antd';
import { transitionDuration } from 'components/layout/layoutStyled';
import { Button } from 'components/uielements';
import themes from 'config/themes';
import translate from 'languageProvider/inline';
import { AiOutlineSmile } from 'react-icons/ai';
import { all, call, cancelled, delay, put, spawn, takeEvery, takeLatest } from 'redux-saga/effects';
import analyticsActions from 'redux/analytics/actions';
import storeObject from 'redux/storeDeclaration';
import AppThemeProvider from 'components/shared/AppThemeProvider';
import sidebarActions from '../sider/actions';
import actions from './actions';
import verifyCheckpointRequest, { finishOnboardingRequest } from './requests';

//ATS joyride docs @ https://wiki.poslovi.live/en/ats/joyride

function* verifyCheckpointGenerator(action) {
  try {
    const response = yield call(verifyCheckpointRequest, action.payload);
    if (response.data.show) {
      yield put({
        type: actions.VERIFY_CHECKPOINT_DONE,
        payload: {
          section: action.payload.section,
          state: response.data.show,
          // Where to resume. Null means start from the top, which is what every section other
          // than selectionLevelsApplications always gets back.
          nextStep: response.data.nextStep ?? null,
        },
      });
    }
  } catch (e) {
    yield put({ type: actions.VERIFY_CHECKPOINT_ERROR });
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.VERIFY_CHECKPOINT_CANCELLED,
      });
    }
  }
}

function* verifyCheckpointFlagGenerator(action) {
  try {
    const response = yield call(verifyCheckpointRequest, action.payload);
    if (response.data.show) {
      yield put({
        type: actions.VERIFY_CHECKPOINT_FLAG_DONE,
        payload: {
          section: action.payload.section,
          nextStep: response.data.nextStep ?? null,
        },
      });
    }
  } catch (e) {
    yield put({ type: actions.VERIFY_CHECKPOINT_FLAG_ERROR });
  }
}

function* verifyCheckpointWithNotificationGenerator(action) {
  try {
    const response = yield call(verifyCheckpointRequest, action.payload);
    const store = storeObject.getStore();
    yield call(() => {
      if (response.data.show) {
        const selectedTheme = store.getState().ThemeSwitcher.get('selectedTheme');

        store.dispatch({
          type: analyticsActions.REDUX_TRACK_EVENT,
          payload: {
            category: 'onboarding',
            action: 'onboarding-request-notification',
          },
        });
        notification.open({
          title: translate('tour.tools.notification.title'),
          description: translate('tour.tools.notification.description'),
          duration: 0,
          key: 'tour notification',
          icon: <AiOutlineSmile style={{ color: '#108ee9' }} />,
          onClose: () => {
            store.dispatch({
              type: actions.TOUR_RUN_SECTION,
              payload: {
                section: action.payload.section,
                state: actions.CANCELL,
              },
            });
            store.dispatch({
              type: analyticsActions.REDUX_TRACK_EVENT,
              payload: {
                category: 'onboarding',
                action: 'close_X',
                label: 'notification',
              },
            });
          },
          btn: (
            <AppThemeProvider theme={themes[selectedTheme]}>
              <Button
                onClick={() => {
                  notification.destroy('tour notification');
                  store.dispatch({
                    type: actions.TOUR_RUN_SECTION,
                    payload: {
                      section: action.payload.section,
                      state: actions.CANCELL,
                    },
                  });
                  store.dispatch({
                    type: analyticsActions.REDUX_TRACK_EVENT,
                    payload: {
                      category: 'onboarding',
                      action: 'close_no',
                      label: 'notification',
                    },
                  });
                }}
              >
                {translate('tour.notification.cancel')}
              </Button>
              <Button
                type="primary"
                style={{
                  marginLeft: '10px',
                }}
                onClick={() => {
                  store.dispatch({
                    type: actions.VERIFY_CHECKPOINT_DONE,
                    payload: {
                      section: action.payload.section,
                      state: response.data.show,
                    },
                  });
                  notification.destroy('tour notification');
                  store.dispatch({
                    type: analyticsActions.REDUX_TRACK_EVENT,
                    payload: {
                      category: 'onboarding',
                      action: 'run',
                      label: 'notification',
                    },
                  });
                }}
              >
                {translate('tour.notification.run')}
              </Button>
            </AppThemeProvider>
          ),
        });
      }
    });
  } catch (e) {
    yield put({ type: actions.VERIFY_CHECKPOINT_ERROR });
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.VERIFY_CHECKPOINT_CANCELLED,
      });
    }
  }
}

function* finishOnboardingGenerator(action) {
  try {
    if ([actions.CANCELL, actions.STOP].indexOf(action.payload.state) !== -1) {
      if (action.payload.section && !action.payload.silent) {
        yield call(finishOnboardingRequest, {
          // A tour whose backend section differs from its key in the store passes
          // `onboardingSection` — two joyride sections can report progress into one backend
          // section that way.
          section: action.payload.onboardingSection || action.payload.section,
          step: action.payload.step,
        });
      }
      if (actions.CANCELL === action.payload.state) {
        yield put({
          type: sidebarActions.TOGGLE_SIDEBAR,
        });
        yield delay(transitionDuration);
        yield put({
          type: actions.RUN_CANCEL_TOUR,
          payload: {
            state: actions.RUN,
          },
        });
      }
    }
  } catch (e) {
    log(e);
  }
}

export function* markAsFinishedGenerator(action) {
  if (action.payload.section) {
    yield call(finishOnboardingRequest, {
      section: action.payload.section,
    });
  }
}

export function* verifyCheckpoint() {
  yield takeEvery(actions.VERIFY_CHECKPOINT_START, verifyCheckpointGenerator);
}

export function* verifyCheckpointFlag() {
  yield takeEvery(actions.VERIFY_CHECKPOINT_FLAG_START, verifyCheckpointFlagGenerator);
}

export function* verifyCheckpointWithNotification() {
  yield takeLatest(
    actions.VERIFY_CHECKPOINT_WITH_NOTIFICATION_START,
    verifyCheckpointWithNotificationGenerator
  );
}

export function* finishOnboarding() {
  yield takeLatest(actions.TOUR_RUN_SECTION, finishOnboardingGenerator);
}

export function* markAsFinished() {
  yield takeEvery(actions.FINISH_CHECKPOINT, markAsFinishedGenerator);
}

export default function* rootSaga() {
  yield all([
    spawn(verifyCheckpoint),
    spawn(finishOnboarding),
    spawn(verifyCheckpointWithNotification),
    spawn(verifyCheckpointFlag),
    spawn(markAsFinished),
  ]);
}
